import { useMemo } from 'react';
import { resolveMediaUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { buildSiteHierarchyGroups, type SiteTreeMeta } from '@/lib/catalogSiteTree';

export type SiteSubscribePublishOverride = {
  subscribe_title?: string;
  subscribe_body?: string;
  subscribe_mp_qr_path?: string;
  publish_title?: string;
  publish_body?: string;
  publish_service_qr_path?: string;
};

type Props = {
  sites: SiteTreeMeta[];
  siteIds: number[];
  pages: Record<string, SiteSubscribePublishOverride>;
  onPatch: (siteId: number, patch: Partial<SiteSubscribePublishOverride>) => void;
  onClear: (siteId: number) => void;
  onPickSubscribeQr: (siteId: number, file: File) => void | Promise<void>;
  onPickPublishQr: (siteId: number, file: File) => void | Promise<void>;
};

const detailsClass =
  'rounded-lg border border-slate-200 bg-white shadow-sm open:ring-1 open:ring-slate-200/80 [&_summary::-webkit-details-marker]:hidden';
const summaryClass =
  'flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-slate-50/80';

function hasOverride(pages: Record<string, SiteSubscribePublishOverride>, siteId: number): boolean {
  return Object.prototype.hasOwnProperty.call(pages, String(siteId));
}

function SubPubFields({
  o,
  onPatch,
  onPickSubscribeQr,
  onPickPublishQr,
  siteId,
}: {
  siteId: number;
  o: SiteSubscribePublishOverride;
  onPatch: (p: Partial<SiteSubscribePublishOverride>) => void;
  onPickSubscribeQr: (file: File) => void | Promise<void>;
  onPickPublishQr: (file: File) => void | Promise<void>;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-3">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">订阅（本站）</h4>
        <label className={labelClass}>
          <span>页面标题</span>
          <input
            className={inputClass}
            value={o.subscribe_title ?? ''}
            placeholder="留空则用上级默认"
            onChange={(e) => onPatch({ subscribe_title: e.target.value })}
          />
        </label>
        <label className={`${labelClass} mt-2`}>
          <span>说明文字</span>
          <textarea
            className={`${inputClass} min-h-[80px]`}
            value={o.subscribe_body ?? ''}
            placeholder="留空则用上级默认"
            onChange={(e) => onPatch({ subscribe_body: e.target.value })}
          />
        </label>
        <label className={`${labelClass} mt-2`}>
          <span>公众号二维码</span>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {o.subscribe_mp_qr_path ? (
              <img
                src={resolveMediaUrl(o.subscribe_mp_qr_path)}
                alt=""
                className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
              />
            ) : (
              <span className="text-xs text-slate-500">未上传</span>
            )}
            <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">
              上传
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickSubscribeQr(f);
                }}
              />
            </label>
          </div>
        </label>
      </div>
      <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-3">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">发布（本站）</h4>
        <label className={labelClass}>
          <span>页面标题</span>
          <input
            className={inputClass}
            value={o.publish_title ?? ''}
            placeholder="留空则用上级默认"
            onChange={(e) => onPatch({ publish_title: e.target.value })}
          />
        </label>
        <label className={`${labelClass} mt-2`}>
          <span>说明文字</span>
          <textarea
            className={`${inputClass} min-h-[80px]`}
            value={o.publish_body ?? ''}
            placeholder="留空则用上级默认"
            onChange={(e) => onPatch({ publish_body: e.target.value })}
          />
        </label>
        <label className={`${labelClass} mt-2`}>
          <span>客服二维码</span>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {o.publish_service_qr_path ? (
              <img
                src={resolveMediaUrl(o.publish_service_qr_path)}
                alt=""
                className="h-24 w-24 rounded-lg border border-slate-200 object-cover"
              />
            ) : (
              <span className="text-xs text-slate-500">未上传</span>
            )}
            <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">
              上传
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickPublishQr(f);
                }}
              />
            </label>
          </div>
        </label>
      </div>
    </div>
  );
}

export function SiteSubscribePublishHierarchyEditor({
  sites,
  siteIds,
  pages,
  onPatch,
  onClear,
  onPickSubscribeQr,
  onPickPublishQr,
}: Props) {
  const hierarchyGroups = useMemo(() => buildSiteHierarchyGroups(sites.filter((s) => siteIds.includes(s.id))), [sites, siteIds]);

  function renderSite(siteId: number, siteName: string, depth: 0 | 1, parentName?: string) {
    const inheriting = depth === 1 && !hasOverride(pages, siteId);
    const override = hasOverride(pages, siteId);
    const o = pages[String(siteId)] || {};

    return (
      <details key={siteId} className={cn(detailsClass, depth === 1 && 'border-l-4 border-l-teal-500/35')}>
        <summary className={summaryClass}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <SiteLevelBadge depth={depth} className={depth === 1 ? '!px-1 !py-px !text-[9px]' : undefined} />
            <span className="truncate font-medium text-slate-900">{siteName}</span>
            {depth === 1 ? (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  inheriting ? 'bg-slate-100 text-slate-600' : 'bg-teal-50 text-teal-900 ring-1 ring-teal-200/80',
                )}
              >
                {inheriting ? '沿用一级' : '已单独配置'}
              </span>
            ) : override ? (
              <span className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-900 ring-1 ring-teal-200/80">
                已单独配置
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                沿用全站默认
              </span>
            )}
          </span>
          <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
        </summary>
        <div className="border-t border-slate-100 px-3 pb-3 pt-2">
          {depth === 1 && inheriting && parentName ? (
            <p className="mb-3 rounded-lg border border-teal-200/80 bg-teal-50/90 px-3 py-2 text-xs text-teal-950">
              当前沿用一级「{parentName}」。填写任意项将保存为本站配置；可点「恢复沿用一级」。
            </p>
          ) : null}
          {override ? (
            <div className="mb-2 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!text-teal-800"
                onClick={() => onClear(siteId)}
              >
                {depth === 1 ? '恢复沿用一级' : '恢复沿用全站默认'}
              </Button>
            </div>
          ) : (
            <p className="mb-2 text-xs text-slate-500">修改任意字段将自动保存为本站单独配置。</p>
          )}
          <SubPubFields
            siteId={siteId}
            o={o}
            onPatch={(p) => onPatch(siteId, p)}
            onPickSubscribeQr={(f) => onPickSubscribeQr(siteId, f)}
            onPickPublishQr={(f) => onPickPublishQr(siteId, f)}
          />
        </div>
      </details>
    );
  }

  if (siteIds.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {hierarchyGroups.map((g, gi) => {
        const parent = g.parent?.site;
        const parentId = parent?.id ?? 0;
        const parentName = parent?.site_name ?? '';
        if (parent && parentId > 0 && g.children.length > 0) {
          return (
            <details key={`sp-${gi}-${parentId}`} className={detailsClass}>
              <summary className={cn(summaryClass, 'bg-slate-50/60')}>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <SiteLevelBadge depth={0} />
                  <span className="truncate font-semibold text-slate-900">{parent.site_name}</span>
                  <span className="shrink-0 rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                    含 {g.children.length} 个二级
                  </span>
                </span>
                <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
              </summary>
              <div className="space-y-3 border-t border-slate-100 p-3">
                <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-700">一级站点（二级默认沿用）</p>
                  <SubPubFields
                    siteId={parentId}
                    o={pages[String(parentId)] || {}}
                    onPatch={(p) => {
                      if (!hasOverride(pages, parentId) && Object.keys(p).some((k) => String((p as Record<string, string>)[k] || '').trim())) {
                        onPatch(parentId, p);
                      } else {
                        onPatch(parentId, p);
                      }
                    }}
                    onPickSubscribeQr={(f) => onPickSubscribeQr(parentId, f)}
                    onPickPublishQr={(f) => onPickPublishQr(parentId, f)}
                  />
                  {hasOverride(pages, parentId) ? (
                    <div className="mt-2 flex justify-end">
                      <Button type="button" variant="ghost" size="sm" className="!text-teal-800" onClick={() => onClear(parentId)}>
                        清除一级单独配置
                      </Button>
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">二级站点</p>
                  {g.children.map((c) => renderSite(c.site.id, c.site.site_name, 1, parentName))}
                </div>
              </div>
            </details>
          );
        }
        if (parent) {
          return renderSite(parentId, parent.site_name, 0);
        }
        return (
          <div key={`sp-o-${gi}`} className="space-y-2">
            {g.children.map((c) => renderSite(c.site.id, c.site.site_name, 1))}
          </div>
        );
      })}
    </div>
  );
}
